import React from "react";
import { Box, IconButton, Typography, Tooltip } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Field from "./Field";
import { pageScrollStyle } from "../../../../../styles/styles";

const Section = ({
  section,
  index,
  isDragging,
  draggedItemRef,
  activeSectionDropIndex,
  setActiveSectionDropIndex,
  handleSectionDragStart,
  handleFieldDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleSectionReorderDrop,
  handleUpdateSectionName,
  handleRemoveSection,
  handleSectionMenuOpen,
  handleFieldDrop,
  handleFieldReorderDrop,
  handleUpdateFieldLabel,
  handleFieldMenuOpen,
  activeFieldDropData,
  setActiveFieldDropData,
  getFieldWidth,
  dragTarget,
  handleUpdateFieldProperty,
  isDefaultSection,
  isFieldAvailableForDefaultSection,
}) => {
  return (
    <Box key={section.id}>
      {isDragging &&
        draggedItemRef.current?.type === "section" &&
        activeSectionDropIndex === index && (
          <Box
            onDragOver={handleDragOver}
            onDragEnter={() => setActiveSectionDropIndex(index)}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setActiveSectionDropIndex(null);
              }
            }}
            onDrop={(e) => handleSectionReorderDrop(e, index)}
            sx={{
              height: "5rem",
              borderRadius: "0.5rem",
              border: "1px dashed #e0e0e0",
              backgroundColor: "rgba(76, 175, 80, 0.1)",
              transition: "all 0.2s ease",
              mb: "0.5rem",
            }}
          />
        )}
      <Box
        draggable={!isDefaultSection}
        onDragStart={(e) => handleSectionDragStart(e, section.id)}
        onDragEnd={handleDragEnd}
        onDragEnter={() => {
          if (isDragging && draggedItemRef.current?.type === "section") {
            setActiveSectionDropIndex(index);
          }
        }}
        sx={{
          width: isDefaultSection ? "40rem" : "100%",
          height: isDefaultSection ? "30rem" : "100%",
          overflow: "scroll",
          borderRadius: "0.5rem",
          border: "1px solid #e0e0e0",
          p: "0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          mb: "1rem",
          cursor: isDefaultSection ? "default" : "grab",
          backgroundColor: isDefaultSection ? "#f5f5f5" : "transparent",
          overflowX: "hidden",
          ...(isDefaultSection ? pageScrollStyle : {}),
        }}
      >
        {/* Section header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
            pb: "0.5rem",
          }}
        >
          <input
            type="text"
            value={section.name}
            onChange={(e) =>
              handleUpdateSectionName(section.id, e.target.value)
            }
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "0.2rem",
              padding: "0.2rem",
              fontSize: "0.8rem",
              width: "50%",
            }}
            disabled={isDefaultSection}
            readOnly={section?.isEditableSection === false}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => handleSectionMenuOpen(e, section.id)}
              disabled={isDefaultSection}
            >
              <SettingsOutlinedIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
            {!isDefaultSection && (
              <Tooltip title="Remove Section" size="small">
                <IconButton
                  size="small"
                  sx={{ color: "#f44336" }}
                  disabled={section?.isEditableSection === false}
                  onClick={() => handleRemoveSection(section.id)}
                >
                  <CloseRoundedIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Field drop area */}
        <Box
          onDragOver={handleDragOver}
          onDragEnter={(e) => handleDragEnter(e, "field", section.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleFieldDrop(e, section.id)}
          sx={{
            minHeight: "5rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            alignItems: "flex-start",
            position: "relative",
          }}
        >
          {/* Render fields in this section */}
          {section.fields.map((field, fieldIndex) => (
            <Field
              key={field.id}
              field={field}
              fieldIndex={fieldIndex}
              section={section}
              isDragging={isDragging}
              draggedItemRef={draggedItemRef}
              activeFieldDropData={activeFieldDropData}
              setActiveFieldDropData={setActiveFieldDropData}
              handleFieldDragStart={handleFieldDragStart}
              handleDragEnd={handleDragEnd}
              handleDragOver={handleDragOver}
              handleFieldReorderDrop={handleFieldReorderDrop}
              handleUpdateFieldLabel={handleUpdateFieldLabel}
              handleFieldMenuOpen={handleFieldMenuOpen}
              getFieldWidth={getFieldWidth}
              dragTarget={dragTarget}
              isDefaultSection={isDefaultSection}
            />
          ))}

          {/* Message when section is empty */}
          {section.fields.length === 0 && !isDragging && (
            <Box
              sx={{
                width: "100%",
                textAlign: "center",
                color: "#757575",
                fontSize: "0.8rem",
                fontStyle: "italic",
                py: "1rem",
              }}
            >
              {isDefaultSection
                ? "Drag and drop fields here (each field can only be used once)"
                : "Drag and drop fields here"}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Section;
